import React, { useMemo, useState } from "react";
import Icon from "components/AppIcon";
import useAuth from "hooks/useAuth";

function getInitials(name, username) {
  const source = String(name || username || "User").trim();
  if (!source) return "U";

  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();

  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

function formatValue(value, fallback = "Not provided") {
  const safeValue = String(value || "").trim();
  return safeValue || fallback;
}

function DetailItem({ label, value }) {
  return (
    <div className="rounded-lg border border-border bg-background px-4 py-3">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-sm font-semibold text-foreground break-words">{value}</p>
    </div>
  );
}

function EmptyState({ children }) {
  return (
    <div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-6 text-center text-sm text-muted-foreground">
      {children}
    </div>
  );
}

function PillList({ items, getLabel, emptyText }) {
  if (!items.length) {
    return <EmptyState>{emptyText}</EmptyState>;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item) => {
        const label = getLabel(item);
        return (
          <span
            key={item?.id || label}
            className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700"
            title={label}
          >
            <span className="truncate">{label}</span>
          </span>
        );
      })}
    </div>
  );
}

function OrgUnitList({ title, icon, items, emptyText }) {
  const [searchTerm, setSearchTerm] = useState("");
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredItems = useMemo(() => {
    if (!normalizedSearch) return items;

    return items.filter((unit) => {
      const searchable = [
        unit?.name,
        unit?.level,
        unit?.path,
        unit?.parent?.name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchable.includes(normalizedSearch);
    });
  }, [items, normalizedSearch]);

  return (
    <section className="rounded-lg border border-border bg-card p-5 shadow-sm">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-2">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#16325a]/10 text-[#16325a]">
            <Icon name={icon} className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <h2 className="truncate text-base font-semibold text-foreground">{title}</h2>
            <p className="text-xs text-muted-foreground">
              {items.length} assigned
              {normalizedSearch && ` / ${filteredItems.length} shown`}
            </p>
          </div>
        </div>

        {items.length > 0 && (
          <div className="relative w-full sm:w-56">
            <Icon
              name="Search"
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            />
            <input
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Filter units"
              className="h-9 w-full rounded-lg border border-border bg-background pl-9 pr-3 text-sm text-foreground outline-none transition focus:border-[#16325a] focus:ring-2 focus:ring-[#16325a]/15"
            />
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <EmptyState>{emptyText}</EmptyState>
      ) : filteredItems.length === 0 ? (
        <EmptyState>No organisation units match "{searchTerm}".</EmptyState>
      ) : (
        <div className="max-h-[22rem] overflow-y-auto pr-1">
          <div className="space-y-2">
          {filteredItems.map((unit) => (
            <div
              key={unit?.id || unit?.name}
              className="rounded-lg border border-border bg-background px-3.5 py-3"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-semibold text-foreground">{formatValue(unit?.name)}</p>
                {unit?.level && (
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                    Level {unit.level}
                  </span>
                )}
              </div>
              {unit?.parent?.name && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Parent: {unit.parent.name}
                </p>
              )}
            </div>
          ))}
          </div>
        </div>
      )}
    </section>
  );
}

export default function SettingsPage() {
  const { user } = useAuth();
  const profile = user?.user || {};
  const fullName = formatValue(profile.fullName || profile.displayName, "Unknown user");
  const username = formatValue(profile.username);
  const roles = Array.isArray(profile.userRoles) ? profile.userRoles : [];
  const organisationUnits = Array.isArray(profile.organisationUnits) ? profile.organisationUnits : [];
  const dataViewOrganisationUnits = Array.isArray(profile.dataViewOrganisationUnits)
    ? profile.dataViewOrganisationUnits
    : [];
  const userGroups = Array.isArray(profile.userGroups) ? profile.userGroups : [];
  const isDisabled = Boolean(profile.disabled);

  return (
    <div className="main-content">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">User Settings</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Account identity, access roles, and assigned organisation units.
        </p>
      </div>

      <section className="mb-6 rounded-lg border border-border bg-card p-5 shadow-sm">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 items-center gap-4">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-[#16325a] text-lg font-bold text-white">
              {getInitials(fullName, username)}
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="truncate text-xl font-bold text-foreground">{fullName}</h2>
                <span
                  className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                    isDisabled
                      ? "bg-red-50 text-red-700 border border-red-100"
                      : "bg-emerald-50 text-emerald-700 border border-emerald-100"
                  }`}
                >
                  {isDisabled ? "Disabled" : "Active"}
                </span>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">@{username}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:min-w-[28rem]">
            <DetailItem label="Roles" value={roles.length} />
            <DetailItem label="Org Units" value={organisationUnits.length} />
            <DetailItem label="Data View" value={dataViewOrganisationUnits.length} />
            <DetailItem label="Groups" value={userGroups.length} />
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
        <section className="rounded-lg border border-border bg-card p-5 shadow-sm">
          <div className="mb-4 flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#16325a]/10 text-[#16325a]">
              <Icon name="UserRound" className="h-4 w-4" />
            </span>
            <h2 className="text-base font-semibold text-foreground">Account Details</h2>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <DetailItem label="Display Name" value={fullName} />
            <DetailItem label="Username" value={username} />
            <DetailItem label="Email" value={formatValue(profile.email)} />
            <DetailItem label="Phone Number" value={formatValue(profile.phone || profile.phoneNumber)} />
          </div>
        </section>

        <div className="space-y-6">
          <section className="rounded-lg border border-border bg-card p-5 shadow-sm">
            <div className="mb-4 flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#16325a]/10 text-[#16325a]">
                <Icon name="ShieldCheck" className="h-4 w-4" />
              </span>
              <h2 className="text-base font-semibold text-foreground">User Roles</h2>
            </div>
            <PillList
              items={roles}
              getLabel={(role) => formatValue(role?.name, "Unnamed role")}
              emptyText="No user roles assigned."
            />
          </section>

          <section className="rounded-lg border border-border bg-card p-5 shadow-sm">
            <div className="mb-4 flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#16325a]/10 text-[#16325a]">
                <Icon name="UsersRound" className="h-4 w-4" />
              </span>
              <h2 className="text-base font-semibold text-foreground">User Groups</h2>
            </div>
            <PillList
              items={userGroups}
              getLabel={(group) => formatValue(group?.name, "Unnamed group")}
              emptyText="No user groups assigned."
            />
          </section>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
        <OrgUnitList
          title="Organisation Units"
          icon="Network"
          items={organisationUnits}
          emptyText="No organisation units assigned."
        />
        <OrgUnitList
          title="Data View Organisation Units"
          icon="Eye"
          items={dataViewOrganisationUnits}
          emptyText="No data view organisation units assigned."
        />
      </div>
    </div>
  );
}
